import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "../axios-orders";
import { queryKeys } from "./queryKeys";
import {
  ServiceToQuote,
  CreatePurchaseRequestV2Payload,
  PurchaseRequestItemV2,
  PurchaseRequestItemV2PaginatedResponse,
  PurchaseRequestV2,
  PurchaseRequestV2PaginatedResponse,
  PurchaseRequestStatus,
} from "../types";

const baseUrl = "/api/purchase-requests";
type RequestFilters = { status?: string; buyerId?: string; size?: number };
type ItemFilters = {
  state?: string;
  buyerId?: string;
  size?: number;
};

interface CategoryFilterParams {
  page: number;
  categoryIds: string[];
  size?: number;
  buyerId?: string;
}

interface TagFilterParams {
  page: number;
  tagIds: string[];
  size?: number;
  buyerId?: string;
}

interface UpdateStatusRequest {
  status: PurchaseRequestStatus;
  rejectedMessage?: string;
}

const buildQueryParams = (filters?: RequestFilters | ItemFilters) => {
  const params = new URLSearchParams();
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value && value !== "ALL") {
      params.append(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

const onGetMyRequests = async (
  page: number,
  filters?: RequestFilters
): Promise<PurchaseRequestV2PaginatedResponse> => {
  const { data } = await axios.get(
    `${baseUrl}/me/${page}${buildQueryParams(filters)}`
  );
  return data;
};

const onGetRequesterRequests = async (
  page: number,
  filters?: RequestFilters
): Promise<PurchaseRequestV2PaginatedResponse> => {
  const { data } = await axios.get(
    `${baseUrl}/me/requester/${page}${buildQueryParams(filters)}`
  );
  return data;
};

const onGetCompanyRequests = async (
  page: number,
  filters?: RequestFilters
): Promise<PurchaseRequestV2PaginatedResponse> => {
  const { data } = await axios.get(
    `${baseUrl}/company/${page}${buildQueryParams(filters)}`
  );
  return data;
};

const onGetRequest = async (id: string): Promise<PurchaseRequestV2> => {
  const { data } = await axios.get(`${baseUrl}/${id}`);
  return data;
};

const onGetItemsByIds = async (
  itemIds: string[]
): Promise<PurchaseRequestItemV2[]> => {
  const params = new URLSearchParams();
  itemIds.forEach((id) => params.append("itemIds", id));
  const { data } = await axios.get(`${baseUrl}/items?${params.toString()}`);
  return data;
};

const onCreateRequest = async (
  body: CreatePurchaseRequestV2Payload
): Promise<PurchaseRequestV2> => {
  const { data } = await axios.post(baseUrl, body);
  return data;
};

const onUpdateRequest = async ({
  id,
  body,
}: {
  id: string;
  body: Partial<PurchaseRequestV2>;
}): Promise<PurchaseRequestV2> => {
  const { data } = await axios.put(`${baseUrl}/${id}`, body);
  return data;
};

const onDeleteRequest = async (id: string): Promise<void> => {
  await axios.delete(`${baseUrl}/${id}`);
};

const onUpdateRequestItem = async ({
  itemId,
  body,
}: {
  itemId: string;
  body: PurchaseRequestItemV2;
}): Promise<PurchaseRequestItemV2> => {
  const { data } = await axios.put(`${baseUrl}/items/${itemId}`, body);
  return data;
};

const onDeleteRequestItem = async (itemId: string): Promise<void> => {
  await axios.delete(`${baseUrl}/items/${itemId}`);
};

const onGetBuyerItems = async (
  page: number,
  filters?: ItemFilters
): Promise<PurchaseRequestItemV2PaginatedResponse> => {
  const { data } = await axios.get(
    `${baseUrl}/items/buyer/${page}${buildQueryParams(filters)}`
  );
  return data;
};

const onGetCompanyItems = async (
  page: number,
  buyerIds: string[],
  state?: string,
  size: number = 10,
  companyIds: string[] = [],
  requestingSiteId?: string
): Promise<PurchaseRequestItemV2PaginatedResponse> => {
  const params = new URLSearchParams({ size: size.toString() });

  // Add multiple buyerId params
  buyerIds.forEach((id) => params.append("buyerId", id));

  // Add multiple companyId params (multi-company view, General #4)
  companyIds.forEach((id) => params.append("companyIds", id));

  // Add state filter if provided and not "ALL"
  if (state && state !== "ALL") {
    params.append("state", state);
  }

  if (requestingSiteId) params.append("requestingSiteId", requestingSiteId);

  const { data } = await axios.get(
    `${baseUrl}/company/items/${page}?${params.toString()}`
  );
  return data;
};

const onGetAvailablePRCategories = async (buyerId?: string) => {
  const params = new URLSearchParams();
  if (buyerId) {
    params.append("buyerId", buyerId);
  }
  const queryString = params.toString();
  const { data } = await axios.get(
    `${baseUrl}/company/available-categories${queryString ? `?${queryString}` : ""}`
  );
  return data;
};

export const useRequesterPurchaseRequestsV2 = (
  page: number,
  status?: string,
  enabled: boolean = true,
  size = 10
) =>
  useQuery({
    queryKey: [
      queryKeys.PURCHASE_REQUESTS_V2_ME_REQUESTER,
      page,
      status || "ALL",
      size,
    ],
    queryFn: () => onGetRequesterRequests(page, { status, size }),
    enabled,
  });

export const useMyPurchaseRequestsV2 = (
  page: number,
  status?: string,
  enabled: boolean = true,
  size = 10
) =>
  useQuery({
    queryKey: [queryKeys.PURCHASE_REQUESTS_V2_ME, page, status || "ALL", size],
    queryFn: () => onGetMyRequests(page, { status, size }),
    enabled,
  });

export const useCompanyPurchaseRequestsV2 = (
  page: number,
  status?: string,
  buyerId?: string,
  enabled: boolean = true,
  size = 10
) =>
  useQuery({
    queryKey: [
      queryKeys.PURCHASE_REQUESTS_V2_COMPANY,
      page,
      status || "ALL",
      buyerId || "ALL",
      size,
    ],
    queryFn: () => onGetCompanyRequests(page, { status, buyerId, size }),
    enabled,
  });

export const usePurchaseRequestV2 = (
  id: string | undefined,
  enabled: boolean = true
) =>
  useQuery({
    queryKey: [queryKeys.PURCHASE_REQUEST_V2, id],
    queryFn: () => onGetRequest(id || ""),
    enabled: !!id && enabled,
  });

/** SCRUM-115: the services this request's items were quoted as ("Servicios a cotizar"). */
const onGetServicesToQuote = async (requestId: string): Promise<ServiceToQuote[]> => {
  const { data } = await axios.get(
    `/api/purchase-requests/${requestId}/services-to-quote`
  );
  return data;
};

export const useServicesToQuote = (requestId: string | undefined) =>
  useQuery({
    queryKey: [queryKeys.PURCHASE_REQUEST_V2_SERVICES_TO_QUOTE, requestId],
    queryFn: () => onGetServicesToQuote(requestId as string),
    enabled: !!requestId,
  });

export const useItemsByIds = (itemIds: string[], enabled: boolean = true) =>
  useQuery({
    queryKey: [queryKeys.PURCHASE_REQUEST_V2_ITEMS, "byIds", itemIds],
    queryFn: () => onGetItemsByIds(itemIds),
    enabled: enabled && itemIds.length > 0,
  });

export const useCreatePurchaseRequestV2 = (
  onSuccess: (res: PurchaseRequestV2) => void,
  onError?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onCreateRequest,
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUESTS_V2_ME],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUESTS_V2_ME_REQUESTER],
      });
      onSuccess(res);
    },
    onError,
  });
};

export const useUpdatePurchaseRequestV2 = (
  onSuccess: (res: PurchaseRequestV2) => void,
  onError?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onUpdateRequest,
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUEST_V2, res.id],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUESTS_V2_ME],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUESTS_V2_COMPANY],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUESTS_V2_ME_REQUESTER],
      });
      onSuccess(res);
    },
    onError,
  });
};

export const useDeletePurchaseRequestV2 = (
  onSuccess: () => void,
  onError?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onDeleteRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUESTS_V2_ME],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUESTS_V2_COMPANY],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUESTS_V2_ME_REQUESTER],
      });
      onSuccess();
    },
    onError,
  });
};

export const useUpdateRequestItemV2 = (
  onSuccess: (res: PurchaseRequestItemV2) => void,
  onError?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onUpdateRequestItem,
    onSuccess: (res) => {
      const requestId = res.request?.id;
      if (requestId) {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.PURCHASE_REQUEST_V2, requestId],
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.PURCHASE_REQUEST_V2_ITEMS, requestId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUEST_V2_BUYER_ITEMS],
      });
      onSuccess(res);
    },
    onError,
  });
};

export const useDeleteRequestItemV2 = (
  onSuccess: () => void,
  onError?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onDeleteRequestItem,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUEST_V2],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUEST_V2_ITEMS],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUEST_V2_BUYER_ITEMS],
      });
      onSuccess();
    },
    onError,
  });
};

export const useBuyerItemsQueueV2 = (
  page: number,
  state?: string,
  buyerId?: string,
  enabled: boolean = true,
  size = 10
) =>
  useQuery({
    queryKey: [
      queryKeys.PURCHASE_REQUEST_V2_BUYER_ITEMS,
      page,
      state || "ALL",
      buyerId || "ALL",
      size,
    ],
    queryFn: () => onGetBuyerItems(page, { state, buyerId, size }),
    enabled,
  });

export const useCompanyItemsV2 = (
  page: number,
  buyerIds: string[],
  state?: string,
  enabled: boolean = true,
  companyIds: string[] = [],
  requestingSiteId?: string,
  size = 10
) =>
  useQuery({
    queryKey: [
      queryKeys.PURCHASE_REQUEST_V2_COMPANY_ITEMS,
      page,
      state || "ALL",
      buyerIds.sort().join(","),
      [...companyIds].sort().join(","),
      requestingSiteId || "ALL",
      size,
    ],
    queryFn: () =>
      onGetCompanyItems(
        page,
        buyerIds,
        state,
        size,
        companyIds,
        requestingSiteId
      ),
    enabled: enabled && (buyerIds.length > 0 || companyIds.length > 0),
  });

export const useAvailablePRCategories = (buyerId?: string) =>
  useQuery({
    queryKey: [
      queryKeys.PURCHASE_REQUEST_V2_AVAILABLE_CATEGORIES,
      buyerId || "ALL",
    ],
    queryFn: () => onGetAvailablePRCategories(buyerId),
  });

const onFilterByCategories = async (
  params: CategoryFilterParams
): Promise<PurchaseRequestV2PaginatedResponse> => {
  const { page, categoryIds, size = 10, buyerId } = params;
  const searchParams = new URLSearchParams({ size: size.toString() });
  categoryIds.forEach((id) => searchParams.append("categoryIds", id));
  if (buyerId) {
    searchParams.append("buyerId", buyerId);
  }

  const { data } = await axios.get(
    `${baseUrl}/company/${page}/categories?${searchParams}`
  );
  return data;
};

const onFilterItemsByCategories = async (
  params: CategoryFilterParams
): Promise<PurchaseRequestItemV2PaginatedResponse> => {
  const { page, categoryIds, size = 10, buyerId } = params;
  const searchParams = new URLSearchParams({ size: size.toString() });
  categoryIds.forEach((id) => searchParams.append("categoryIds", id));
  if (buyerId) {
    searchParams.append("buyerId", buyerId);
  }

  const { data } = await axios.get(
    `${baseUrl}/company/items/${page}/categories?${searchParams}`
  );
  return data;
};

const onGetAvailablePRTags = async (buyerId?: string): Promise<any[]> => {
  const params = buyerId ? `?buyerId=${buyerId}` : "";
  const { data } = await axios.get(
    `${baseUrl}/company/available-tags${params}`
  );
  return data;
};

const onGetAvailableTagsForRequest = async (
  requestId: string
): Promise<any[]> => {
  const { data } = await axios.get(`${baseUrl}/${requestId}/available-tags`);
  return data;
};

const onFilterRequestItemsByTags = async (
  requestId: string,
  tagIds: string[]
): Promise<PurchaseRequestItemV2[]> => {
  const searchParams = new URLSearchParams();
  tagIds.forEach((id) => searchParams.append("tagIds", id));
  const { data } = await axios.get(
    `${baseUrl}/${requestId}/items/tags?${searchParams}`
  );
  return data;
};

export interface RequestItemFilterParams {
  tagIds?: string[];
  categoryIds?: string[];
  assignedBuyerIds?: string[];
  states?: string[];
}

const onFilterRequestItems = async (
  requestId: string,
  filters: RequestItemFilterParams
): Promise<PurchaseRequestItemV2[]> => {
  const searchParams = new URLSearchParams();
  (filters.tagIds ?? []).forEach((id) => searchParams.append("tagIds", id));
  (filters.categoryIds ?? []).forEach((id) =>
    searchParams.append("categoryIds", id)
  );
  (filters.assignedBuyerIds ?? []).forEach((id) =>
    searchParams.append("assignedBuyerIds", id)
  );
  (filters.states ?? []).forEach((state) =>
    searchParams.append("states", state)
  );
  const { data } = await axios.get(
    `${baseUrl}/${requestId}/items/filter?${searchParams}`
  );
  return data;
};

const onGetAvailableAssignedBuyersForRequest = async (
  requestId: string
): Promise<any[]> => {
  const { data } = await axios.get(
    `${baseUrl}/${requestId}/available-assigned-buyers`
  );
  return data;
};

const onGetAvailableCategoriesForRequest = async (
  requestId: string
): Promise<any[]> => {
  const { data } = await axios.get(
    `${baseUrl}/${requestId}/available-categories`
  );
  return data;
};

const onSearchRequestItems = async (
  requestId: string,
  query: string
): Promise<PurchaseRequestItemV2[]> => {
  const searchParams = new URLSearchParams();
  if (query) {
    searchParams.append("query", query);
  }
  const { data } = await axios.get(
    `${baseUrl}/${requestId}/items/search?${searchParams}`
  );
  return data;
};

const onFilterByTags = async (
  params: TagFilterParams
): Promise<PurchaseRequestV2PaginatedResponse> => {
  const { page, tagIds, size = 10, buyerId } = params;
  const searchParams = new URLSearchParams({ size: size.toString() });
  tagIds.forEach((id) => searchParams.append("tagIds", id));
  if (buyerId) {
    searchParams.append("buyerId", buyerId);
  }

  const { data } = await axios.get(
    `${baseUrl}/company/${page}/tags?${searchParams}`
  );
  return data;
};

const onFilterItemsByTags = async (
  params: TagFilterParams
): Promise<PurchaseRequestItemV2PaginatedResponse> => {
  const { page, tagIds, size = 10, buyerId } = params;
  const searchParams = new URLSearchParams({ size: size.toString() });
  tagIds.forEach((id) => searchParams.append("tagIds", id));
  if (buyerId) {
    searchParams.append("buyerId", buyerId);
  }

  const { data } = await axios.get(
    `${baseUrl}/company/items/${page}/tags?${searchParams}`
  );
  return data;
};

const onUpdateStatus = async ({
  prId,
  request,
}: {
  prId: string;
  request: UpdateStatusRequest;
}): Promise<PurchaseRequestV2> => {
  const { data } = await axios.put(`${baseUrl}/${prId}/status`, request);
  return data;
};

export const useFilterByCategories = (params: CategoryFilterParams) =>
  useQuery({
    queryKey: [queryKeys.PURCHASE_REQUESTS_V2_BY_CATEGORIES, params],
    queryFn: () => onFilterByCategories(params),
    enabled: params.categoryIds.length > 0,
  });

export const useFilterItemsByCategories = (params: CategoryFilterParams) =>
  useQuery({
    queryKey: [queryKeys.PURCHASE_REQUEST_V2_ITEMS_BY_CATEGORIES, params],
    queryFn: () => onFilterItemsByCategories(params),
    enabled: params.categoryIds.length > 0,
  });

export const useAvailablePRTags = (buyerId?: string) =>
  useQuery({
    queryKey: [queryKeys.PURCHASE_REQUEST_V2_AVAILABLE_TAGS, buyerId || "ALL"],
    queryFn: () => onGetAvailablePRTags(buyerId),
  });

export const useFilterByTags = (params: TagFilterParams) =>
  useQuery({
    queryKey: [queryKeys.PURCHASE_REQUESTS_V2_BY_TAGS, params],
    queryFn: () => onFilterByTags(params),
    enabled: params.tagIds.length > 0,
  });

export const useFilterItemsByTags = (params: TagFilterParams) =>
  useQuery({
    queryKey: [queryKeys.PURCHASE_REQUEST_V2_ITEMS_BY_TAGS, params],
    queryFn: () => onFilterItemsByTags(params),
    enabled: params.tagIds.length > 0,
  });

export const useAvailableTagsForRequest = (requestId?: string) =>
  useQuery({
    queryKey: [
      queryKeys.PURCHASE_REQUEST_V2_AVAILABLE_TAGS_FOR_REQUEST,
      requestId,
    ],
    queryFn: () => onGetAvailableTagsForRequest(requestId!),
    enabled: !!requestId,
  });

export const useFilterRequestItemsByTags = (
  requestId?: string,
  tagIds?: string[]
) =>
  useQuery({
    queryKey: [
      queryKeys.PURCHASE_REQUEST_V2_ITEMS_BY_TAGS_FOR_REQUEST,
      requestId,
      tagIds,
    ],
    queryFn: () => onFilterRequestItemsByTags(requestId!, tagIds!),
    enabled: !!requestId && !!tagIds && tagIds.length > 0,
  });

export const useFilterRequestItems = (
  requestId: string | undefined,
  filters: RequestItemFilterParams
) => {
  const hasAnyFilter =
    (filters.tagIds?.length ?? 0) > 0 ||
    (filters.categoryIds?.length ?? 0) > 0 ||
    (filters.assignedBuyerIds?.length ?? 0) > 0 ||
    (filters.states?.length ?? 0) > 0;

  return useQuery({
    queryKey: [
      queryKeys.PURCHASE_REQUEST_V2_ITEMS_FILTER,
      requestId,
      filters,
    ],
    queryFn: () => onFilterRequestItems(requestId!, filters),
    enabled: !!requestId && hasAnyFilter,
    // Keep the previous filter result visible while the new one loads so the
    // table doesn't flash empty / show the unfiltered list when the user
    // toggles between filter selections.
    placeholderData: keepPreviousData,
  });
};

export const useAvailableAssignedBuyersForRequest = (
  requestId: string | undefined
) =>
  useQuery({
    queryKey: [
      queryKeys.PURCHASE_REQUEST_V2_AVAILABLE_ASSIGNED_BUYERS,
      requestId,
    ],
    queryFn: () => onGetAvailableAssignedBuyersForRequest(requestId!),
    enabled: !!requestId,
  });

/** A request company's active members: who an item of it can be reassigned to. */
export interface AssignableBuyer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const onGetAssignableBuyers = async (
  requestId: string
): Promise<AssignableBuyer[]> => {
  const { data } = await axios.get(
    `${baseUrl}/${requestId}/assignable-buyers`
  );
  return data;
};

export const useAssignableBuyers = (requestId: string | undefined) =>
  useQuery({
    queryKey: [queryKeys.PURCHASE_REQUEST_V2_ASSIGNABLE_BUYERS, requestId],
    queryFn: () => onGetAssignableBuyers(requestId!),
    enabled: !!requestId,
  });

const onReassignItemBuyer = async ({
  itemId,
  buyerId,
}: {
  itemId: string;
  buyerId: string | null;
}): Promise<PurchaseRequestItemV2> => {
  const { data } = await axios.put(
    `${baseUrl}/items/${itemId}/assigned-buyer`,
    { buyerId }
  );
  return data;
};

/** Sets (or clears, with `buyerId: null`) the buyer of one item, then refreshes its request. */
export const useReassignItemBuyer = (
  requestId: string | undefined,
  onSuccess: (res: PurchaseRequestItemV2) => void,
  onError?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onReassignItemBuyer,
    onSuccess: (res) => {
      if (requestId) {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.PURCHASE_REQUEST_V2, requestId],
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.PURCHASE_REQUEST_V2_ITEMS, requestId],
        });
        queryClient.invalidateQueries({
          queryKey: [
            queryKeys.PURCHASE_REQUEST_V2_AVAILABLE_ASSIGNED_BUYERS,
            requestId,
          ],
        });
      }
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUEST_V2_BUYER_ITEMS],
      });
      onSuccess(res);
    },
    onError,
  });
};

export const useAvailableCategoriesForRequest = (
  requestId: string | undefined
) =>
  useQuery({
    queryKey: [
      queryKeys.PURCHASE_REQUEST_V2_AVAILABLE_CATEGORIES_FOR_REQUEST,
      requestId,
    ],
    queryFn: () => onGetAvailableCategoriesForRequest(requestId!),
    enabled: !!requestId,
  });

export const useSearchRequestItems = (requestId?: string, query?: string) =>
  useQuery({
    queryKey: [queryKeys.PURCHASE_REQUEST_V2_ITEMS_SEARCH, requestId, query],
    queryFn: () => onSearchRequestItems(requestId!, query!),
    enabled: !!requestId && !!query && query.trim().length > 0,
    // Same rationale as `useFilterRequestItems`: don't flicker the table while
    // the user is typing in the search box.
    placeholderData: keepPreviousData,
  });

export const useUpdatePRStatus = (
  onSuccess?: (data: PurchaseRequestV2) => void,
  onError?: (error: any) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onUpdateStatus,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUEST_V2],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUESTS_V2_COMPANY],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUESTS_V2_ME],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUESTS_V2_ME_REQUESTER],
      });
      onSuccess?.(data);
    },
    onError,
  });
};
